import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { dispatchApi } from '../../../shared/lib/api'
import { useAuthStore } from '../../../shared/store/authStore'
import TabletHeader from '../../../shared/components/TabletHeader'
import TabletScreen from '../../../shared/components/TabletScreen'
import { Badge, LoadingSpinner } from '../../../shared/components/UI'

export default function DispatchListPage() {
  const { user }  = useAuthStore()
  const queryClient = useQueryClient()
  const [search, setSearch]   = useState('')

  const { data: dispatchData, isLoading, error } = useQuery({
    queryKey: ['dispatch-list', search],
    queryFn: () => dispatchApi.list({ customerName: search }).then(res => res.data.data),
  })

  const { mutate: dispatchMutate, isPending: isDispatching } = useMutation({
    mutationFn: (id) => dispatchApi.markDispatched(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['dispatch-list'])
    },
  })

  const batches = dispatchData || []

  return (
    <TabletScreen>
      <TabletHeader title="Dispatch List" role="Loading Supervisor" unit={user?.unitName || 'Unit'} teal />
      <div className="p-4">

        {/* Search */}
        <div className="relative mb-4">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" width="15" height="15" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M10.5 10.5l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          <input
            className="form-input pl-9 text-sm"
            placeholder="Search by customer name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Count */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">{batches.length} batches</p>
          <Badge variant="amber">{batches.filter(b => !b.is_dispatched).length} pending</Badge>
        </div>

        {/* Loading / Error states */}
        {isLoading && <div className="text-center py-10"><LoadingSpinner size="lg" teal /></div>}
        {error && <div className="text-center py-10 text-red-500 text-sm">Failed to load dispatch list</div>}

        {/* Batch cards */}
        <div className="flex flex-col gap-3">
          {!isLoading && batches.length === 0 && (
            <div className="text-center py-12 text-gray-400 text-sm">No batches ready for dispatch</div>
          )}
          {batches.map((b) => {
            const batchInfo = b.coneReturn?.coneHandoff?.boilingLog?.spindleOutput?.spindleLoading?.batch
            return (
              <div
                key={b.id}
                className={`bg-white border rounded-2xl p-4 transition-colors ${b.is_dispatched ? 'border-green-200 bg-green-50/40' : 'border-gray-100'}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-sm font-bold text-brand-600">{batchInfo?.batch_number || '---'}</span>
                  <Badge variant={b.is_dispatched ? 'green' : 'amber'}>{b.is_dispatched ? 'Dispatched' : 'Ready'}</Badge>
                </div>

                <p className="text-sm font-medium text-gray-800 mb-0.5">{b.customer_name || 'Customer Not Assigned'}</p>
                <p className="text-xs text-gray-500 mb-3">
                  {batchInfo?.shade?.name || '---'} · {new Date(b.created_at).toLocaleDateString()}
                </p>

                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-gray-50 rounded-lg p-2 text-center shadow-sm">
                    <p className="text-[10px] text-gray-400">Weight</p>
                    <p className="text-sm font-semibold text-gray-700">{b.coneReturn?.return_weight_kg || '0'} KG</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2 text-center shadow-sm">
                    <p className="text-[10px] text-gray-400">Rolls</p>
                    <p className="text-sm font-semibold text-gray-700">{b.coneReturn?.jari_roll_count || '0'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2 text-center shadow-sm">
                    <p className="text-[10px] text-gray-400">Ready</p>
                    <p className="text-sm font-semibold text-gray-700">{new Date(b.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}</p>
                  </div>
                </div>

                {!b.is_dispatched && b.customer_name && (
                  <button
                    disabled={isDispatching}
                    onClick={() => dispatchMutate(b.id)}
                    className="w-full py-3 rounded-xl bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition-colors shadow-sm disabled:opacity-50"
                  >
                    {isDispatching ? 'Updating...' : 'Mark as Dispatched'}
                  </button>
                )}
                
                {!b.is_dispatched && !b.customer_name && (
                  <p className="text-[10px] text-amber-600 text-center italic bg-amber-50 py-2 rounded-lg">Wait for Admin to assign customer</p>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </TabletScreen>
  )
}
